import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { jobsAPI } from '../lib/api';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Search, Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';

export const DashboardJobSearch = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setOpen(false);
      return;
    }
    const timeout = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await jobsAPI.getPaginated({
          search: query.trim(),
          status: 'all',
          page: 1,
          page_size: 8,
        });
        setResults(res.data.items || []);
        setOpen(true);
      } catch (e) {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [query]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative w-full sm:w-72" data-testid="dashboard-job-search">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="Search by job number..."
          className="pl-9 font-mono text-sm"
          data-testid="dashboard-job-search-input"
        />
        {searching && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
        )}
      </div>
      {open && (
        <div
          className="absolute z-50 mt-1 w-full rounded-lg border bg-popover shadow-lg overflow-hidden"
          data-testid="dashboard-job-search-results"
        >
          {results.length === 0 ? (
            <p className="p-3 text-sm text-muted-foreground">No jobs found</p>
          ) : (
            results.map((job) => (
              <button
                key={job.id}
                onClick={() => {
                  setOpen(false);
                  setQuery('');
                  navigate(`/jobs/${job.id}`);
                }}
                className="w-full text-left px-3 py-2.5 hover:bg-muted transition-colors border-b last:border-b-0"
                data-testid={`job-search-result-${job.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium truncate">{job.title}</span>
                  <Badge variant="outline" className="text-xs shrink-0">{job.status}</Badge>
                </div>
                <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                  {job.job_number && <span className="font-mono">{job.job_number}</span>}
                  {job.start_time && <span>{format(parseISO(job.start_time), 'MMM d, yyyy')}</span>}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};
